import React, { ChangeEvent, FC } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers'
import { ISelectOption } from 'components/forms/Selects'
import { IAddNewEntityFormProps } from 'apps/fleet-planner/components/forms/TrailerForm'
import {
  EditCustomerSchema,
  EditCustomerSchemaType,
  IUpdatedCustomer,
} from 'apps/fleet-planner/schema/EditCustomerSchema'
import * as Types from 'apps/fleet-planner/types'
import { Button, TextArea } from 'transfix-ui'

const ADD_NEW_CONTACT = 'Add New Contact'

/**
 * Returns array of options
 */
export const getCustomerContactOptions = (
  customer: Partial<Types.ICustomer>
): ISelectOption<number>[] => {
  const { contacts = [] } = customer || {}
  const options = contacts.map(({ email, id: value = 0, name: label, phone }) => ({
    label: label || `No Contact Name: ${email || phone || ''}`,
    value,
  }))

  return [...options, { label: ADD_NEW_CONTACT, value: -1 }]
}

const emptyCustomerState: EditCustomerSchemaType = {
  notes: '',
  contact: undefined,
}

const getDefaultCustomerValues = (customer: IUpdatedCustomer): EditCustomerSchemaType => ({
  notes: customer.notes ?? '',
  id: customer?.id,
})

interface IEditCustomerProps extends IAddNewEntityFormProps<EditCustomerSchemaType> {}

export const EditCustomerForm: FC<IEditCustomerProps> = ({
  onSuccess: onSuccessProp,
  onCancel: onCancelProp = () => {},
  defaultValues: defaultValuesProp = {},
  primaryButtonText: primaryButtonTextProp = 'Save Changes',
  isLoading: isLoadingProp,
}) => {
  const defaultValues = getDefaultCustomerValues(defaultValuesProp)
  const { control, errors, handleSubmit } = useForm({
    mode: 'all',
    resolver: yupResolver(EditCustomerSchema),
    defaultValues: EditCustomerSchema.cast({
      ...emptyCustomerState,
      ...defaultValues,
    }),
  })

  const submitHandler = (data: EditCustomerSchemaType) => {
    onSuccessProp(data)
  }

  return (
    <form onSubmit={handleSubmit(submitHandler)}>
      {/* Customer notes */}
      <div className='mb4'>
        <Controller
          name='notes'
          control={control}
          render={({ onBlur, onChange, value }) => {
            const changeHandler = (e: ChangeEvent<HTMLTextAreaElement>) => {
              onChange(e.target.value)
            }

            return (
              <TextArea
                placeholder='Write internal notes about this customer.'
                label='Customer Notes'
                size='large'
                dataTest='customer-notes-input'
                value={value}
                onBlur={onBlur}
                onChange={changeHandler}
                invalid={!!errors.notes?.message}
              />
            )
          }}
        />
      </div>
      <div className='db cf tr mb5'>
        <Button
          color='tertiary'
          size='large'
          type='button'
          onClick={onCancelProp}
          data-test='cancel-edit-customer'>
          Cancel
        </Button>
        <Button
          color='primary'
          disabled={isLoadingProp}
          size='large'
          type='submit'
          data-test='save-edit-customer'>
          {primaryButtonTextProp}
        </Button>
      </div>
    </form>
  )
}
